/**
 * ToastProvider + ToastHost + useToast (P35.2 / #556).
 *
 * Tiny in-memory toast primitive. No vendor library. Fires from
 * any feature module via useToast(); renders as a fixed-position
 * stack at the top-end of the viewport via <ToastHost />.
 *
 * Auto-dismisses after 5 seconds (default). Operator can dismiss
 * earlier by clicking the close button.
 *
 * 4 tones (info / success / warning / error) wired to the existing
 * slate / emerald / amber / rose palette used elsewhere.
 *
 * Mount once: <ToastProvider> wraps App.tsx, <ToastHost /> renders
 * inside AppShell.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "shared/api/client";
import { setMutationFailureSink } from "shared/api/queryClient";
import { refusalMessage } from "shared/api/refusalMessage";

export type ToastTone = "info" | "success" | "warning" | "error";

export interface Toast {
  id: string;
  tone: ToastTone;
  message: string;
  /** Optional sub-line shown smaller beneath the message. */
  detail?: string;
  /** D1 -- one thing the reader can do about what just happened.
   *
   *  Accepting an Engine suggestion overwrote a preparer's judgement
   *  in silence, and the moment she needs to reverse it is the moment
   *  she is told. Deliberately ONE action: a toast is a confirmation,
   *  not a menu. */
  action?: { label: string; onClick: () => void };
}

interface ToastContextValue {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id"> & { id?: string }) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_AUTO_DISMISS_MS = 5_000;

interface ToastProviderProps {
  children: ReactNode;
  autoDismissMs?: number;
}

export function ToastProvider({
  children,
  autoDismissMs = DEFAULT_AUTO_DISMISS_MS,
}: ToastProviderProps) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const toastsRef = useRef<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    toastsRef.current = toastsRef.current.filter((t) => t.id !== id);
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  // Round 17 A32 -- the same sentence, twice, from one failure (the CAP
  // drafter's refusal, the derive's): two paths each announced it. One
  // visible copy of an identical toast is enough; the second refreshes
  // the first's timer instead of stacking. A toast with an action is
  // never folded -- its offer belongs to the act that raised it.

  const push = useCallback(
    (toast: Omit<Toast, "id"> & { id?: string }) => {
      if (toast.id === undefined && toast.action === undefined) {
        const same = toastsRef.current.find(
          (x) =>
            x.action === undefined &&
            x.tone === toast.tone &&
            x.message === toast.message &&
            x.detail === toast.detail,
        );
        if (same) {
          const old = timersRef.current.get(same.id);
          if (old) clearTimeout(old);
          if (autoDismissMs > 0) {
            timersRef.current.set(
              same.id,
              setTimeout(() => dismiss(same.id), autoDismissMs),
            );
          }
          return same.id;
        }
      }
      const id =
        toast.id ?? `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const next: Toast = {
        id,
        tone: toast.tone,
        message: toast.message,
        ...(toast.detail !== undefined ? { detail: toast.detail } : {}),
        ...(toast.action !== undefined ? { action: toast.action } : {}),
      };
      // Synchronously, so two pushes in one tick see each other.
      toastsRef.current = [...toastsRef.current.filter((t) => t.id !== id), next];
      setToasts((prev) => [...prev.filter((t) => t.id !== id), next]);
      /*
       * Round 9 E5 -- a toast carrying an ACTION waits to be dismissed.
       *
       * Accepting an Engine suggestion overwrites a preparer's
       * judgement with three paragraphs the Engine wrote, and the only
       * route back is the Undo on the toast announcing it. That toast
       * faded after five seconds -- less time than it takes to read
       * what just replaced your work and decide whether you want it.
       * Undo lived only as long as the confirmation of the thing it
       * undoes.
       *
       * A confirmation may fade: it is telling you something you
       * already know you did. An OFFER cannot, because its whole value
       * is being there when the reader reaches for it. The dismiss
       * button and the action itself both close it, so nothing
       * accumulates -- and this is a property of carrying an action
       * rather than a per-call-site flag, so the next one inherits it.
       */
      if (autoDismissMs > 0 && next.action === undefined) {
        const timer = setTimeout(() => dismiss(id), autoDismissMs);
        timersRef.current.set(id, timer);
      }
      return id;
    },
    [autoDismissMs, dismiss],
  );

  // A1 -- the sink the data layer announces unhandled write failures
  // through. Set here because this is where a toast can actually be
  // raised, and read there because a module-level QueryClient cannot
  // call a hook.
  useEffect(() => {
    setMutationFailureSink((message, error) => {
      // ROUND 15 A10 -- "That did not save" over an Engine refusal, when
      // nothing was being saved. A refusal carries its own sentence and a
      // code; say that, in the reader's language, and nothing else.
      if (error instanceof ApiError && error.code) {
        push({ tone: "error", message: refusalMessage(t, error) });
        return;
      }
      push({ tone: "error", message: t("common.writeFailed"), detail: message });
    });
  }, [push, t]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({ toasts, push, dismiss }),
    [toasts, push, dismiss],
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

/** Round 16 O7 -- for shared components that confirm a result only
 *  where the app has mounted a provider; tests that render them bare
 *  keep working. */
export function useOptionalToast() {
  return useContext(ToastContext);
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (ctx === null) {
    throw new Error(
      "useToast() called outside ToastProvider. Wrap App in <ToastProvider> at the composition root.",
    );
  }
  return ctx;
}

const TONE_CLASSES: Record<ToastTone, string> = {
  info: "border-slate-300 bg-slate-50 text-slate-900",
  success: "border-emerald-300 bg-emerald-50 text-emerald-900",
  warning: "border-amber-300 bg-amber-50 text-amber-900",
  error: "border-rose-300 bg-rose-50 text-rose-900",
};

const TONE_DOT: Record<ToastTone, string> = {
  info: "bg-slate-500",
  success: "bg-emerald-500",
  warning: "bg-amber-500",
  error: "bg-rose-500",
};

export function ToastHost() {
  const { toasts, dismiss } = useToast();
  // `t2`, because `t` is the per-toast loop variable below and
  // shadowing the translator there is how strings go missing.
  const { t: t2 } = useTranslation();
  // E2 -- the region exists BEFORE anything is put in it.
  //
  // This returned null while empty, so the live region was created at
  // the same instant as its first message. A screen reader that has
  // not been watching a region when its content arrives commonly says
  // nothing at all -- which is the same defect as announcing twice,
  // from the other end. The container is fixed-position and empty, so
  // an always-mounted host costs nothing visually.
  return (
    /* E2 (round 8) -- ONE live region, not one inside another.
       The container announced politely and every item inside it also
       carried role="status", which implies aria-live="polite". A live
       region nested in a live region is announced by BOTH, so every
       toast in the product reached a screen reader -- and the
       walkthrough's accessibility tree -- twice: the answered toast,
       grant minted, grant revoked, examiner pack downloaded, narrative
       pass queued and mock-exam pack downloaded, all of them, from one
       cause.
       The container keeps the region, because it is what persists
       across toasts appearing and going; the items are its content. */
    <div
      data-testid="toast-host"
      /* Round 11 B4 -- how many toasts there are, said as a number.
         Eight rounds have carried a doubled-toast finding and the
         cause has been the same since round 8's E1 closed the real
         one: a container whose text is its children's text, read by a
         selector that matches both. Measured on this build, one push
         mints one item with one id, and `[data-testid*=toast]` returns
         the sentence twice and a "×" -- because that selector matches
         the host, the item AND the dismiss button.
         Nothing can stop an ancestor containing its descendant's text.
         What can be stopped is having to infer the count from it. */
      data-count={toasts.length}
      aria-live="polite"
      aria-atomic="false"
      className="fixed top-4 end-4 z-50 flex flex-col gap-2"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          data-testid="toast-item"
          data-toast-id={t.id}
          data-tone={t.tone}
          className={
            "flex items-start gap-2 rounded border px-3 py-2 text-sm shadow-md min-w-[260px] max-w-[360px] " +
            TONE_CLASSES[t.tone]
          }
        >
          <span
            aria-hidden="true"
            className={"mt-1 inline-block h-2 w-2 rounded-full " + TONE_DOT[t.tone]}
          />
          <div className="flex-1">
            <p className="font-semibold">{t.message}</p>
            {t.detail && <p className="mt-0.5 text-xs opacity-80">{t.detail}</p>}
            {t.action && (
              <button
                type="button"
                data-testid={`toast-action-${t.id}`}
                onClick={() => {
                  t.action?.onClick();
                  dismiss(t.id);
                }}
                className="mt-1 text-xs font-semibold underline underline-offset-2"
              >
                {t.action.label}
              </button>
            )}
          </div>
          <button
            type="button"
            data-testid="toast-dismiss"
            onClick={() => dismiss(t.id)}
            aria-label={t2("common.dismiss")}
            className="opacity-60 hover:opacity-100"
          >
            {/* Round 11 B4 -- drawn, not typed. As a text glyph the
                "×" was a third entry in every text extraction of this
                region, sitting beside the sentence it belongs to and
                reading like part of it. It carries no meaning a reader
                needs in text: the button's name is its aria-label. */}
            <svg
              viewBox="0 0 10 10"
              aria-hidden="true"
              className="h-2.5 w-2.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            >
              <path d="M1 1l8 8M9 1l-8 8" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
}
